import { component, prop/*, debounce*/, proxy } from '../../decorators';

import KeyboardHelper from '../../helpers/keyboard.helper';

import Timeline from '../common/timeline/timeline.vue';
import Flow from '../common/flow/flow.vue';
import VerticalScroll from '../common/vertical-scroll/vertical-scroll.vue';
import WorkplacePosition from './workplace-position/workplace-position.vue';
import TimecodeInput from '../common/timecode-input/timecode-input.vue';
import FlowHeaders from './flow-headers/flow-headers.vue';

import storage from '../../helpers/storage.helpers';

import { SYSTEM_BOUNDED_KEYS } from '../system-hotkeys';

import invoke from 'lodash/invoke';
import each from 'lodash/each';
import isNil from 'lodash/isNil';
import negate from 'lodash/negate';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import first from 'lodash/first';
import constant from 'lodash/constant';
import map from 'lodash/map';
import intersection from 'lodash/intersection';

import l10n from '../../transformers/l10n';

import { TIMEDATA_TYPES, START_EPISODE_AT } from '../../config';

const existy = negate(isNil);

const TIMELINE_MARGINS = { top: 16, right: 0, bottom: 7, left: 0 };

const ROW_HEIGHT = 33;
const MIN_ROWS_COUNT = 1;

const SCALED_SECOND_WIDTH = 1;
const DEFAULT_SCALE_VALUE = 1;

const STORAGE_GROUP = 'workplace';
const STORAGE_SCALE  = 'scale';

const MS_IN_SEC = 1000;

@component({
   components: { Timeline, Flow, VerticalScroll, WorkplacePosition, TimecodeInput, FlowHeaders },
   watch: {
      currentTime: 'onTimeChanged',
      followTime: 'onTimeChanged',
      selectedType: 'onTypeSelected'
   }
})
export default class Workplace {
   @prop({ type: Array, required: true }) info
   @prop({ type: Number, required: true }) currentTime
   @prop({ type: Object }) clip
   @prop({ type: Object }) currentItem
   @prop({ type: Object, required: true }) episode
   @prop({ type: Number, default: constant(ROW_HEIGHT) }) rowHeight
   @prop({ type: Boolean, default: true }) canSelectRow

   @prop({ type: Number }) tcIn
   @prop({ type: Number }) tcOut

   @prop({ type: Boolean, required: true }) followTime

   period = []
   timelineMargins = { ...TIMELINE_MARGINS }
   rendered = false
   scale = DEFAULT_SCALE_VALUE
   keyListeners = []

   headerScrollTop = null

   processingItems = []

   selectedType = 'Clips'
   appearedTypes = []

   expandedTypes = ['Clips']

   get types() {
      return TIMEDATA_TYPES.map(t => {
         const name = l10n(t.tk);
         return { ...t, name };
      });
   }

   get canChangePins() {
      return existy(this.tcIn) && existy(this.tcOut);
   }
   get duration() {
      return this.episode.duration;
   }
   get timecode() {
      return this.episode.timecode;
   }
   get width() {
      return Math.floor(this.scale * this.duration * SCALED_SECOND_WIDTH  / MS_IN_SEC);
   }
   get markerPosition() {
      return _position(this.currentTime, this.period, this.width);
   }

   get toggledInfos() {
      return this.info.map(d => {
         const hasInfo = !!d.info.length;
         const info = includes(this.expandedTypes, d.type) ? d.info : [];
         return { ...d, info, hasInfo };
      });
   }

   get filteredInfos() {
      return filter(this.toggledInfos, ({ type }) => includes(this.appearedTypes, type));
   }

   get leftPosition() {
      return _position(this.tcIn, this.period, this.width);
   }

   get rightPosition() {
      return _position(this.tcOut, this.period, this.width);
   }

   /** @type {Function} */
   @proxy('_proxyEmmiter', 'create-new-clip') createNewClip;

   /** @type {Function} */
   @proxy('_proxyEmmiter', 'change-current-time') changeCurrentTime;

   /** @type {Function} */
   @proxy('_proxyEmmiter', 'add-new-item') addNewItem;

   /** @type {Function} */
   @proxy('_proxyEmmiter', 'remove-item') removeItem;

   /** @type {Function} */
   @proxy('_proxyEmmiter', 'next-frame') nextFrame;

   /** @type {Function} */
   @proxy('_proxyEmmiter', 'prev-frame') prevFrame;

   /** @type {Function} */
   @proxy('_proxyEmmiter', 'toggle-play') togglePlay;

   /** @type {Function} */
   @proxy('_proxyEmmiter', 'stop-play') stopPlay;

   /** @type {Function} */
   @proxy('_proxyEmmiter', 'next-clip') nextClip;

   /** @type {Function} */
   @proxy('_proxyEmmiter', 'prev-clip') prevClip;

   /** @type {Function} */
   @proxy('_proxyEmmiter', 'play-forward') playForward;

   /** @type {Function} */
   @proxy('_proxyEmmiter', 'play-backward') playBackward;

   /** @type {Function} */
   @proxy('_proxyEmmiter', 'jump-to-start') toStart;

   /** @type {Function} */
   @proxy('_proxyEmmiter', 'jump-to-end') toEnd;

   calcHeight(data) {
      return (data.length || MIN_ROWS_COUNT) * this.rowHeight;
   }
   refresh() {
      this.rendered = false;
      this.period = [START_EPISODE_AT, this.timecode + this.duration];
      this.$nextTick(() => {
         this.rendered = true;
         this.$nextTick(() => {
            this.onTimeChanged();
            this.$refs.flows.addEventListener('scroll', this.onScrollFlows);
         });
      });
   }
   onTimeChanged() {
      if ( this.followTime ) {
         this.scrollToMarker();
      }
   }
   _getVisiblePeriod() {
      const { scrollLeft, offsetWidth } = this.$refs.scroll.$el;
      const scrollWidth = +this.$refs.scroll.$el.scrollWidth;
      const from = _time(scrollLeft, this.period, scrollWidth);
      const to = _time(scrollLeft + offsetWidth, this.period, scrollWidth);
      return [ from, to ];
   }
   scrollToMarker() {
      this.$refs.scroll.scrollTo(this.markerPosition, true);
   }
   moveToTime(time, atCenter) {
      if (atCenter) {
         const [ from, to ] = this._getVisiblePeriod();
         time -= Math.ceil( (to - from) / 2 ); // eslint-disable-line no-magic-numbers
      }

      const position = _position(time, this.period, this.width);
      invoke(this.$refs, 'scroll.scrollTo', position);
   }
   onSelect({ type, item }) {
      this._proxyEmmiter(type === 'Clips' ? 'select-clip' : 'select-item', item);
   }
   openInfo({ type, item }) {
      if (type === 'Clips') {
         this._proxyEmmiter('open-clip-info', item);
      }
   }
   cutIn() {
      this.emitCutIn(this.currentTime);
   }
   cutOut() {
      this.emitCutOut(this.currentTime);
   }
   emitCutIn(time) {
      this._proxyEmmiter('cut-in', time);
   }
   emitCutOut(time) {
      this._proxyEmmiter('cut-out', time);
   }

   detectScrollLeft() {
      return this.$refs.scroll.$el.scrollLeft;
   }

   _proxyEmmiter(...args) {
      this.$emit('on', args);
   }

   getLeftPosition() {
      return this.$el.getBoundingClientRect().left;
   }

   async removeCurrentItem() {
      const current = this.currentItem || this.clip;
      if ( !current ) {
         return;
      }
      this.removeItem(current);
   }

   onScrollFlows() {
      this.headerScrollTop = this.$refs.flows.scrollTop;
   }

   selectType(type) {
      if ( this.canSelectRow ) {
         if ( !includes(this.appearedTypes, type) ) {
            const all = map(this.types, 'type');
            this.appearedTypes = intersection(all, [ ...this.appearedTypes, type ]);
         }

         this.selectedType = type;
      }
   }

   changeAppeared(types) {
      this.appearedTypes = types;
      this.selectedType = includes(types, this.selectedType) ? this.selectedType : first(types) || '';
   }

   onTypeSelected() {
      if (includes(this.expandedTypes, this.selectedType)) {
         return;
      }
      this.expandedTypes = [...this.expandedTypes, this.selectedType];
   }

   toggleType(type) {
      if (includes(this.expandedTypes, type)) {
         this.expandedTypes = this.expandedTypes.filter(t => t !== type);
      } else {
         this.expandedTypes = [...this.expandedTypes, type];
      }
   }

   changeScale(scale) {
      // const [from, to] = this._getVisiblePeriod();
      this.scale = scale;
      storage.set(STORAGE_GROUP, STORAGE_SCALE, scale);
      this.refresh();
      if ( !this.followTime ) {
         this.$nextTick(() => {
            // const t = _mediana(from, to);
            this.$nextTick(() => this.moveToTime(this.currentTime, true));
         });
      }
   }

   created() {
      this.appearedTypes = map(this.types, 'type');
   }

   mounted() {
      this.$nextTick(this.refresh);
      const opts = { stop: true, prevent: true };
      this.keyListeners.push(KeyboardHelper.keydown(this.addNewItem, SYSTEM_BOUNDED_KEYS.ADD_TO_TIMELINE.code));
      this.keyListeners.push(KeyboardHelper.keydown(this.nextFrame, SYSTEM_BOUNDED_KEYS.NEXT_FRAME.code, opts));
      this.keyListeners.push(KeyboardHelper.keydown(this.prevFrame, SYSTEM_BOUNDED_KEYS.PREV_FRAME.code, opts));
      this.keyListeners.push(KeyboardHelper.keydown(this.stopPlay, SYSTEM_BOUNDED_KEYS.STOP_PLAY.code));
      this.keyListeners.push(KeyboardHelper.keydown(this.togglePlay, SYSTEM_BOUNDED_KEYS.TOGGLE_PLAY.code, opts));
      this.keyListeners.push(KeyboardHelper.keydown(this.playForward, SYSTEM_BOUNDED_KEYS.PLAY_FORWARD.code));
      this.keyListeners.push(KeyboardHelper.keydown(this.playBackward, SYSTEM_BOUNDED_KEYS.PLAY_BACKWARD.code));
      this.keyListeners.push(KeyboardHelper.keydown(this.nextClip, SYSTEM_BOUNDED_KEYS.NEXT_CLIP.code, opts));
      this.keyListeners.push(KeyboardHelper.keydown(this.prevClip, SYSTEM_BOUNDED_KEYS.PREV_CLIP.code, opts));
      this.keyListeners.push(KeyboardHelper.keydown(this.cutIn, SYSTEM_BOUNDED_KEYS.CUT_IN.code));
      this.keyListeners.push(KeyboardHelper.keydown(this.cutOut, SYSTEM_BOUNDED_KEYS.CUT_OUT.code));
      this.keyListeners.push(KeyboardHelper.keydown(this.removeCurrentItem, SYSTEM_BOUNDED_KEYS.REMOVE_ITEM.code));
      this.keyListeners.push(KeyboardHelper.keydown(this.toStart, SYSTEM_BOUNDED_KEYS.MOVE_TO_START.code, opts));
      this.keyListeners.push(KeyboardHelper.keydown(this.toEnd, SYSTEM_BOUNDED_KEYS.MOVE_TO_END.code, opts));
   }

   destroyed() {
      each(this.keyListeners, fn => fn() );
      this.keyListeners.length = 0;

      if (this.$refs.flows) {
         this.$refs.flows.removeEventListener('scroll', this.onScrollFlows);
      }
   }
}

function _position(time, [from, to], width) {
   return width * (time - from) / (to - from);
}

function _time(position, [from, to], width) {
   const duration = to - from;
   return Math.ceil(duration * position / width) + from;
}
